import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const BASE_URL = 'https://katado.design';

type JsonLdType = 'Organization' | 'LocalBusiness' | 'Service' | 'WebSite' | 'Person' | 'FAQPage';

interface JsonLdProps {
  type?: JsonLdType[];
}

const JsonLd = ({ type = ['Organization', 'WebSite', 'LocalBusiness', 'Person'] }: JsonLdProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    // Remove existing JSON-LD scripts
    document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());

    const schemas: object[] = [];

    // Organization Schema
    if (type.includes('Organization')) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Katado Design',
        url: BASE_URL,
        logo: `${BASE_URL}/favicon.ico`,
        description: t('seo.description', 'Online luxury landscape architecture studio specializing in Japandi-inspired garden design. International projects, thoughtful planting, and refined outdoor spaces, all delivered remotely.'),
        founder: {
          '@type': 'Person',
          name: 'Katia Dordevic',
          jobTitle: 'Licensed Landscape Architect',
        },
        sameAs: [
          // Add social media links here when available
        ],
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          url: `${BASE_URL}/contact`,
          availableLanguage: ['English', 'Spanish', 'German', 'French', 'Italian', 'Portuguese', 'Dutch', 'Polish', 'Russian', 'Swedish', 'Danish', 'Serbian', 'Arabic', 'Chinese', 'Japanese', 'Korean'],
        },
      });
    }

    // WebSite Schema with SearchAction
    if (type.includes('WebSite')) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Katado Design',
        url: BASE_URL,
        description: t('seo.description', 'Online luxury landscape architecture studio specializing in Japandi-inspired garden design. International projects, thoughtful planting, and refined outdoor spaces, all delivered remotely.'),
        inLanguage: ['en', 'es', 'de', 'fr', 'it', 'pt', 'nl', 'pl', 'ru', 'sv', 'da', 'sr', 'ar', 'zh', 'ja', 'ko', 'ca'],
      });
    }

    // LocalBusiness Schema for local SEO
    if (type.includes('LocalBusiness')) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'ProfessionalService',
        name: 'Katado Design',
        url: BASE_URL,
        description: t('seo.description', 'Online luxury landscape architecture studio specializing in Japandi-inspired garden design. International projects, thoughtful planting, and refined outdoor spaces, all delivered remotely.'),
        priceRange: '€€€',
        areaServed: [
          { '@type': 'Continent', name: 'Europe' },
          { '@type': 'Continent', name: 'Australia' },
          { '@type': 'Continent', name: 'North America' },
        ],
        serviceType: 'Landscape Architecture',
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Landscape Design Packages',
          itemListElement: [
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Planting Design',
                description: 'Professional planting design for gardens and outdoor spaces.',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Garden Design',
                description: 'Complete garden design with detailed plans.',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Complete Landscape Design',
                description: 'Full landscape architecture services including hardscape and planting.',
              },
            },
          ],
        },
      });
    }

    // Person Schema for Katia
    if (type.includes('Person')) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: 'Katia Dordevic',
        jobTitle: 'Licensed Landscape Architect',
        worksFor: {
          '@type': 'Organization',
          name: 'Katado Design',
        },
        description: 'Licensed landscape architect with over a decade of experience designing residential landscapes across diverse climates and contexts.',
        knowsAbout: ['Landscape Architecture', 'Garden Design', 'Planting Design', 'Japandi Design', 'Sustainable Landscaping'],
      });
    }

    // Add all schemas to document
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());
    };
  }, [t, type]);

  return null;
};

export default JsonLd;
